import { useEffect, useState, useRef, useCallback } from 'react';
import { getAdminHandbook, saveHandbookSection, deleteHandbookSection, splitHandbookContent, splitHandbookPdf, splitHandbookWord, enhanceHandbookSections, GetAdminHandbookOutputType } from 'zite-endpoints-sdk';
import { uploadFile } from 'zite-file-upload-sdk';
import { parseDocx } from './parseDocx';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Sparkles, BookOpen, FileText, Upload, X, FileUp, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { MarkdownEditor } from '@/components/markdown-editor';

type Section = GetAdminHandbookOutputType['sections'][0];
type SplitMode = 'pdf' | 'text';
type ImportMode = 'split' | 'single';

export default function HandbookAdminTab() {
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editSection, setEditSection] = useState<Section | null>(null);
  const [form, setForm] = useState({ title: '', body: '', sectionOrder: 1, status: 'Draft' as 'Draft' | 'Published' });
  const [saving, setSaving] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);
  const [enhancing, setEnhancing] = useState(false);

  // AI Import state
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitMode, setSplitMode] = useState<SplitMode>('pdf');
  const [importMode, setImportMode] = useState<ImportMode>('split');
  const [pastedContent, setPastedContent] = useState('');
  const [splitting, setSplitting] = useState(false);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [splitProgress, setSplitProgress] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import into section (from edit sheet)
  const [sectionDocFile, setSectionDocFile] = useState<File | null>(null);
  const [importingIntoSection, setImportingIntoSection] = useState(false);
  const sectionFileRef = useRef<HTMLInputElement>(null);

  const fileType = docFile
    ? docFile.name.toLowerCase().endsWith('.docx') ? 'docx' : 'pdf'
    : null;

  const sectionFileType = sectionDocFile
    ? sectionDocFile.name.toLowerCase().endsWith('.docx') ? 'docx' : 'pdf'
    : null;

  const load = () => {
    getAdminHandbook({}).then(d => setSections(d.sections)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditSection(null);
    setForm({ title: '', body: '', sectionOrder: sections.length + 1, status: 'Draft' });
    setSectionDocFile(null);
    setSheetOpen(true);
  };

  const openEdit = (s: Section) => {
    setEditSection(s);
    setForm({ title: s.title, body: s.body, sectionOrder: s.sectionOrder, status: s.status as 'Draft' | 'Published' });
    setSectionDocFile(null);
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await saveHandbookSection({ id: editSection?.id, ...form });
      toast.success(editSection ? 'Section updated' : 'Section created');
      setSheetOpen(false);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteHandbookSection({ id }); toast.success('Section deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleEnhanceAll = async () => {
    setEnhancing(true);
    const tid = 'enhance-all';
    toast.loading('AI is enhancing all sections & adding new ones — this takes ~60 seconds…', { id: tid });
    try {
      const result = await enhanceHandbookSections({});
      toast.success(`✓ ${result.message}`, { id: tid });
      load();
    } catch (e: any) {
      toast.error(e.message || 'Enhancement failed', { id: tid });
    } finally {
      setEnhancing(false);
    }
  };

  const publishAll = async () => {
    const drafts = sections.filter(s => s.status === 'Draft');
    if (drafts.length === 0) return;
    setPublishingAll(true);
    const tid = 'publish-all';
    toast.loading(`Publishing ${drafts.length} sections…`, { id: tid });
    try {
      await Promise.all(
        drafts.map(s => saveHandbookSection({ id: s.id, title: s.title, body: s.body, sectionOrder: s.sectionOrder, status: 'Published' }))
      );
      toast.success(`✓ Published ${drafts.length} sections`, { id: tid });
      load();
    } catch {
      toast.error('Failed to publish all sections', { id: tid });
    } finally {
      setPublishingAll(false);
    }
  };

  // Import content from PDF/Word directly into a section body
  const importIntoSection = async (file: File) => {
    if (!editSection) return;
    setImportingIntoSection(true);
    const tid = 'section-import';
    try {
      if (file.name.toLowerCase().endsWith('.docx')) {
        toast.info('Processing Word document…', { id: tid });
        const result = await parseDocx(file, msg => toast.info(msg, { id: tid }));
        toast.info('AI is extracting content…', { id: tid });
        const res = await splitHandbookWord({
          docxUrl: result.docxUrl,
          docxFilename: file.name,
          imageUrls: result.imageUrls,
          singleSection: true,
          targetSectionId: editSection.id,
        });
        if (res.count > 0) {
          // Refresh the section to get new body
          const updated = await getAdminHandbook({});
          const fresh = updated.sections.find(s => s.id === editSection.id);
          if (fresh) setForm(f => ({ ...f, body: fresh.body }));
          toast.success('✓ Content imported from Word document', { id: tid });
        }
      } else {
        toast.info('Uploading PDF…', { id: tid });
        const { fileUrl } = await uploadFile({ data: file, filename: file.name });
        toast.info('AI is extracting content…', { id: tid });
        const res = await splitHandbookPdf({
          pdfUrl: fileUrl,
          pdfFilename: file.name,
          singleSection: true,
          targetSectionId: editSection.id,
        });
        if (res.count > 0) {
          const updated = await getAdminHandbook({});
          const fresh = updated.sections.find(s => s.id === editSection.id);
          if (fresh) setForm(f => ({ ...f, body: fresh.body }));
          toast.success('✓ Content imported from PDF', { id: tid });
        }
      }
      setSectionDocFile(null);
    } catch (e: any) {
      toast.error(e.message || 'Import failed', { id: tid });
    } finally {
      setImportingIntoSection(false);
    }
  };

  const handleSectionFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSectionDocFile(file);
    e.target.value = '';
  };

  const handleSplitText = async () => {
    if (!pastedContent.trim()) { toast.error('Paste some content first'); return; }
    setSplitting(true);
    try {
      const res = await splitHandbookContent({ content: pastedContent, replaceAll: sections.length > 0 });
      toast.success(`✓ Created ${res.count} sections from your content`);
      setSplitOpen(false); setPastedContent(''); load();
    } catch (e: any) { toast.error(e.message || 'Failed to split'); }
    finally { setSplitting(false); }
  };

  const handleSplitDoc = async () => {
    if (!docFile) { toast.error('Please select a file first'); return; }
    setSplitting(true);
    setSplitProgress('');
    const tid = 'doc-import';
    const isSingle = importMode === 'single';
    try {
      if (fileType === 'docx') {
        toast.info('Processing Word document…', { id: tid });
        const result = await parseDocx(docFile, msg => { setSplitProgress(msg); toast.info(msg, { id: tid }); });
        toast.info('AI is reading your document — this may take 30–60 seconds…', { id: tid });
        setSplitProgress('AI is reading your document…');
        const res = await splitHandbookWord({
          docxUrl: result.docxUrl,
          docxFilename: docFile.name,
          imageUrls: result.imageUrls,
          replaceAll: !isSingle && sections.length > 0,
          singleSection: isSingle,
        });
        const imgMsg = result.imageCount > 0 ? ` · ${result.imageCount} image${result.imageCount !== 1 ? 's' : ''} extracted` : '';
        toast.success(`✓ Created ${res.count} section${res.count !== 1 ? 's' : ''} from your document${imgMsg}`, { id: tid });
      } else {
        toast.info('Uploading PDF…', { id: tid });
        setSplitProgress('Uploading PDF…');
        const { fileUrl } = await uploadFile({ data: docFile, filename: docFile.name });
        toast.info('AI is reading your handbook — this may take 30–60 seconds…', { id: tid });
        setSplitProgress('AI is reading your handbook…');
        const res = await splitHandbookPdf({
          pdfUrl: fileUrl,
          pdfFilename: docFile.name,
          replaceAll: !isSingle && sections.length > 0,
          singleSection: isSingle,
        });
        toast.success(`✓ Created ${res.count} section${res.count !== 1 ? 's' : ''} from your PDF`, { id: tid });
      }
      setSplitOpen(false); setDocFile(null); setSplitProgress(''); load();
    } catch (e: any) {
      toast.error(e.message || 'Failed to process document', { id: tid });
    } finally {
      setSplitting(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const isPdf  = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isDocx = file.name.toLowerCase().endsWith('.docx');
    if (isPdf || isDocx) setDocFile(file);
    else toast.error('Please drop a PDF or Word (.docx) file');
  }, []);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  const published = sections.filter(s => s.status === 'Published').length;
  const draftCount = sections.filter(s => s.status === 'Draft').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary" /> Staff Handbook
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">{sections.length} sections · {published} published · {draftCount} drafts</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button variant="outline" size="sm" onClick={() => setSplitOpen(true)}>
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Import
          </Button>
          {sections.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleEnhanceAll} disabled={enhancing}
              className="border-primary/40 text-primary hover:bg-primary/5">
              {enhancing
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Enhancing…</>
                : <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> ✨ Enhance All Sections</>}
            </Button>
          )}
          {draftCount > 0 && (
            <Button variant="outline" size="sm" onClick={publishAll} disabled={publishingAll}>
              {publishingAll ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Publishing…</> : `Publish All (${draftCount})`}
            </Button>
          )}
          <Button size="sm" onClick={openNew}><Plus className="w-3.5 h-3.5 mr-1" /> Add Section</Button>
        </div>
      </div>

      {sections.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg py-16 text-center">
          <BookOpen className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">No handbook sections yet</p>
          <p className="text-xs text-muted-foreground mb-4">Upload your handbook PDF and AI will split it into sections, or add them manually.</p>
          <Button size="sm" onClick={() => setSplitOpen(true)}><Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Import from PDF</Button>
        </div>
      ) : (
        <div className="border border-border rounded-lg divide-y divide-border">
          {sections.map(s => (
            <div key={s.id} className="px-4 py-3 flex items-center gap-3">
              <span className="text-xs text-muted-foreground w-6 shrink-0">{s.sectionOrder}.</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{s.title}</p>
                <p className="text-xs text-muted-foreground truncate">{s.body.slice(0, 80)}...</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${s.status === 'Published' ? 'bg-primary/10 text-primary' : 'border border-border text-muted-foreground'}`}>
                {s.status}
              </span>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(s)}><Pencil className="w-3 h-3" /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete "{s.title}"?</AlertDialogTitle></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDelete(s.id)}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader><SheetTitle>{editSection ? 'Edit Section' : 'New Section'}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Title *</label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Section title" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Order</label>
                <Input type="number" value={form.sectionOrder} onChange={e => setForm(f => ({ ...f, sectionOrder: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>

            {/* Import from document button */}
            {editSection && (
              <div className="border border-dashed border-border rounded-lg p-3">
                <p className="text-xs font-medium text-foreground mb-2 flex items-center gap-1.5">
                  <FileUp className="w-3.5 h-3.5 text-primary" /> Import content from PDF or Word
                </p>
                {sectionDocFile ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 text-xs text-muted-foreground border border-border rounded px-2 py-1.5 truncate">
                      {sectionDocFile.name}
                    </div>
                    <Button size="sm" onClick={() => importIntoSection(sectionDocFile)} disabled={importingIntoSection} className="shrink-0">
                      {importingIntoSection ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Importing…</> : 'Import'}
                    </Button>
                    <button onClick={() => setSectionDocFile(null)} className="text-muted-foreground hover:text-foreground p-1"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <>
                    <input ref={sectionFileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={handleSectionFileChange} />
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => sectionFileRef.current?.click()}>
                      <Upload className="w-3 h-3 mr-1.5" /> Choose PDF or Word file
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1.5">AI will extract all content and replace this section's body</p>
                  </>
                )}
              </div>
            )}

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Body</label>
              <MarkdownEditor content={form.body} onChange={v => setForm(f => ({ ...f, body: v }))} height="dynamic-lg" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v as 'Draft' | 'Published' }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editSection ? 'Update Section' : 'Create Section'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* AI Import Sheet */}
      <Sheet open={splitOpen} onOpenChange={v => { if (!splitting) { setSplitOpen(v); if (!v) { setDocFile(null); setSplitProgress(''); setPastedContent(''); } } }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> AI Import Handbook</SheetTitle>
          </SheetHeader>

          <div className="space-y-4 mt-6">
            {/* Mode tabs */}
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              <button onClick={() => setSplitMode('pdf')} className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-md transition-colors ${splitMode === 'pdf' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                <FileText className="w-3.5 h-3.5" /> Upload PDF / Word
              </button>
              <button onClick={() => setSplitMode('text')} className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-md transition-colors ${splitMode === 'text' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                <Sparkles className="w-3.5 h-3.5" /> Paste Text
              </button>
            </div>

            {splitMode === 'pdf' && (
              <>
                {/* Split vs single section */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setImportMode('split')}
                    className={`flex-1 text-xs py-2 px-3 rounded-lg border transition-colors text-left ${importMode === 'split' ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                  >
                    <p className="font-medium mb-0.5">📑 Split into sections</p>
                    <p>AI divides the document into multiple sections</p>
                  </button>
                  <button
                    onClick={() => setImportMode('single')}
                    className={`flex-1 text-xs py-2 px-3 rounded-lg border transition-colors text-left ${importMode === 'single' ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                  >
                    <p className="font-medium mb-0.5">📄 Single section</p>
                    <p>Import entire document as one section</p>
                  </button>
                </div>

                {importMode === 'split' && sections.length > 0 && (
                  <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg px-3 py-2 text-xs text-amber-600">
                    ⚠ This will replace all {sections.length} existing sections.
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div className="border border-border rounded-lg p-2.5">
                    <p className="font-medium text-foreground mb-0.5">📄 PDF</p>
                    <p>GPT-4o reads the full document and describes any images inline.</p>
                  </div>
                  <div className="border border-primary/30 bg-primary/5 rounded-lg p-2.5">
                    <p className="font-medium text-foreground mb-0.5">📝 Word (.docx)</p>
                    <p>Images are <strong>extracted and embedded</strong> as real files — nothing is lost.</p>
                  </div>
                </div>

                {docFile ? (
                  <div className="flex items-center gap-3 border border-primary/30 bg-primary/5 rounded-lg px-3 py-2.5">
                    <FileText className={`w-4 h-4 shrink-0 ${fileType === 'docx' ? 'text-blue-500' : 'text-primary'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{docFile.name}</p>
                      <p className="text-xs text-muted-foreground">{(docFile.size / 1024 / 1024).toFixed(1)} MB · {fileType === 'docx' ? 'Word document — images will be extracted' : 'PDF'}</p>
                    </div>
                    <button onClick={() => { setDocFile(null); setSplitProgress(''); }} className="text-muted-foreground hover:text-foreground p-1"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div
                    onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
                  >
                    <Upload className="w-7 h-7 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground">Drop your handbook here</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF or Word (.docx) · Max 25MB · or click to browse</p>
                    <input ref={fileInputRef} type="file" accept=".pdf,application/pdf,.docx" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) { setDocFile(f); setSplitProgress(''); } }} />
                  </div>
                )}

                {splitting && splitProgress && <p className="text-xs text-muted-foreground text-center animate-pulse">{splitProgress}</p>}

                <Button onClick={handleSplitDoc} disabled={splitting || !docFile} className="w-full">
                  {splitting
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {fileType === 'docx' ? 'Extracting images & processing…' : 'Reading with AI — please wait…'}</>
                    : <><Sparkles className="w-4 h-4 mr-2" /> {importMode === 'single' ? 'Import as Single Section' : (fileType === 'docx' ? 'Extract Images & Split into Sections' : 'Split PDF into Sections')}</>}
                </Button>
              </>
            )}

            {splitMode === 'text' && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Paste your handbook text below. AI will automatically split it into logical sections.</p>
                <Textarea value={pastedContent} onChange={e => setPastedContent(e.target.value)} placeholder="Paste your handbook content here…" className="min-h-64" />
                <Button onClick={handleSplitText} disabled={splitting || !pastedContent.trim()} className="w-full">
                  {splitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Splitting with AI…</> : <><Sparkles className="w-4 h-4 mr-2" /> Split into Sections</>}
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
